<script lang="ts">
  import { db, type ContactRow } from '$lib/db/db';
  import { parseStoredElements } from '$lib/db/threads';
  import { formatListTime, formatPhone } from '$lib/format';
  import { t } from '$lib/i18n/index.svelte';
  import { elementPreview } from '$lib/preview';
  import { live } from '$lib/state/live';

  import { navigate } from '../../app/router.svelte';
  import Avatar from '../parts/Avatar.svelte';
  import EmptyState from '../parts/EmptyState.svelte';
  import PushNudge from '../parts/PushNudge.svelte';

  // The list orders by the newest thing in the thread — a received event or an
  // item still waiting to be sent — never by sync time, which would flatten the
  // order on every refresh.
  const contacts = live(
    async () =>
      (await db.contacts.toArray()).sort((a, b) => rowTime(b) - rowTime(a) || a.addedAtMs - b.addedAtMs),
    [] as ContactRow[],
  );

  function rowTime(row: ContactRow): number {
    return Math.max(row.lastEventTimeMs ?? row.addedAtMs, row.lastOutboxTimeMs ?? 0);
  }

  function preview(row: ContactRow): string {
    if (row.lastEventJson === null) {
      return '';
    }
    const elements = parseStoredElements(row.lastEventJson);
    const first = elements[0];
    if (first === undefined) {
      return '';
    }
    const prefix = row.lastFromUser ? t('threads.you') : '';
    return prefix + elementPreview(t, first);
  }
</script>

<header class="bar">
  <h1>{t('threads.title')}</h1>
  <div class="actions">
    <button aria-label={t('threads.add')} title={t('threads.add')} onclick={() => navigate({ name: 'add' })}
      >＋</button
    >
    <button
      aria-label={t('settings.title')}
      title={t('settings.title')}
      onclick={() => navigate({ name: 'settings' })}>⚙</button
    >
  </div>
</header>

<!-- Standing, not dismissible: as long as push is off, the way back to it is
     one glance away — a half-conscious refusal must stay findable forever. -->
{#if $contacts.length > 0}
  <PushNudge />
{/if}

{#if $contacts.length === 0}
  <EmptyState
    title={t('threads.empty.title')}
    body={t('threads.empty.body')}
    actionLabel={t('threads.add')}
    onaction={() => navigate({ name: 'add' })}
  />
{:else}
  <ul class="list">
    {#each $contacts as row (row.accountId)}
      <li>
        <button class="row" onclick={() => navigate({ name: 'conversation', accountId: row.accountId })}>
          <Avatar name={row.name} phone={row.phone} />
          <div class="mid">
            <div class="top">
              <span class="name">{row.name.length > 0 ? row.name : formatPhone(row.phone)}</span>
              <span class="time">{rowTime(row) > 0 ? formatListTime(rowTime(row)) : ''}</span>
            </div>
            <div class="bottom">
              <span class="preview">
                {#if row.pendingCount > 0}{t('threads.pending')} · {/if}{preview(row)}
              </span>
              <span class="badges">
                {#if row.status === 'withdrawn'}
                  <span class="tag">{t('threads.readOnly')}</span>
                {:else if row.sendingPaused}
                  <span class="tag">{t('threads.paused')}</span>
                {/if}
                {#if row.unreadCount > 0}
                  <span class="unread">{row.unreadCount}</span>
                {/if}
              </span>
            </div>
          </div>
        </button>
      </li>
    {/each}
  </ul>
{/if}

<style>
  .bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px 8px;
  }
  h1 {
    margin: 0;
    font-size: 24px;
  }
  .actions button {
    border: 0;
    background: transparent;
    font-size: 22px;
    cursor: pointer;
    padding: 6px 8px;
    color: var(--ink);
  }
  .list {
    list-style: none;
    margin: 0;
    padding: 0;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
  .row {
    display: flex;
    gap: 12px;
    width: 100%;
    text-align: left;
    border: 0;
    background: transparent;
    padding: 12px 16px;
    cursor: pointer;
    color: var(--ink);
    font: inherit;
  }
  .row:hover {
    background: var(--surface-2);
  }
  .mid {
    flex: 1;
    min-width: 0;
  }
  .top,
  .bottom {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    align-items: baseline;
  }
  .name {
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .time {
    font-size: 12px;
    color: var(--ink-soft);
    flex-shrink: 0;
  }
  .preview {
    font-size: 14px;
    color: var(--ink-soft);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .badges {
    display: flex;
    gap: 6px;
    align-items: center;
    flex-shrink: 0;
  }
  .tag {
    font-size: 11px;
    background: var(--surface-2);
    color: var(--ink-soft);
    border-radius: 999px;
    padding: 2px 8px;
  }
  .unread {
    background: var(--brand);
    color: var(--brand-ink);
    border-radius: 999px;
    min-width: 20px;
    height: 20px;
    font-size: 12px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 6px;
  }
</style>
